import * as React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import type { LeagueTemplate, TemplatinessStats } from 'api/league'
import { formatDecimal } from 'utils/numbers'
import { shirtImageSrc } from 'utils/shirtSrc'
import TemplatePitchSurface from 'components/structure/TemplatePitchSurface'
import { TEMPLATE_PITCH_POS_ORDER } from 'components/structure/templatePitchLayout'

interface Props {
  template: LeagueTemplate | null
  templatiness: TemplatinessStats | null
  totalTeams: number
  /** Narrow side column — smaller pitch, badges above grass */
  compact?: boolean
}

const LeagueTemplateCard: React.FC<Props> = ({ template, templatiness, totalTeams, compact = false }) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  if (!template?.players?.length) {
    return null
  }

  const byType: Record<number, typeof template.players> = { 1: [], 2: [], 3: [], 4: [] }
  for (const row of template.players) {
    const et = Number(row.player?.element_type ?? 0)
    if (et >= 1 && et <= 4) {
      byType[et].push(row)
    }
  }

  const jacLeaguePct = templatiness != null ? templatiness.xi_jaccard_avg_league * 100 : null
  const showSimilarity = jacLeaguePct != null

  const statPill = (r: (typeof template.players)[0]) => {
    const cnt = typeof r.starts_league_count === 'number' ? r.starts_league_count : 0
    const pct =
      typeof r.starts_league_pct === 'number'
        ? r.starts_league_pct
        : totalTeams > 0
          ? (cnt / totalTeams) * 100
          : 0
    return (
      <Box
        sx={{
          mt: 0.5,
          px: compact ? 0.75 : { xs: 1, sm: 1.15 },
          py: compact ? 0.25 : { xs: 0.35, sm: 0.4 },
          borderRadius: 999,
          backgroundColor: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.92)',
          border: '1px solid',
          borderColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.1)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          whiteSpace: 'nowrap',
        }}
      >
        <Typography
          component="span"
          sx={{
            fontFamily: 'PremierLeagueBold',
            fontSize: compact ? '0.62rem' : { xs: '0.68rem', sm: '0.76rem', md: '0.82rem' },
            lineHeight: 1.2,
          }}
        >
          {cnt}
        </Typography>
        <Box
          sx={{
            width: '1px',
            height: '0.65em',
            backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.18)',
            flexShrink: 0,
          }}
        />
        <Typography
          component="span"
          sx={{
            fontFamily: 'PremierLeagueRegular',
            fontSize: compact ? '0.62rem' : { xs: '0.68rem', sm: '0.76rem', md: '0.82rem' },
            opacity: 0.78,
            lineHeight: 1.2,
          }}
        >
          {formatDecimal(pct)}%
        </Typography>
      </Box>
    )
  }

  const rowOfCells = (rows: typeof template.players) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        flexWrap: 'nowrap',
        gap: compact ? 0.75 : { xs: 1, sm: 1.5, md: 2 },
        width: '100%',
        maxWidth: '100%',
        py: { xs: 0.2, sm: 0.35 },
      }}
    >
      {rows.map((r) => {
        const pid = r.player?.id ?? 0
        const tid = r.player?.team ?? 0
        const isGoalkeeper = Number(r.player?.element_type) === 1
        return (
          <Box
            key={pid}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              minWidth: compact ? 48 : { xs: 64, sm: 76, md: 88 },
              maxWidth: compact ? 72 : { xs: 108, sm: 124, md: 140 },
              flex: rows.length <= 3 ? '0 1 auto' : '1 1 0',
            }}
          >
            <Box
              component="img"
              src={shirtImageSrc(tid, isGoalkeeper)}
              alt=""
              sx={{
                width: compact ? 28 : { xs: 34, sm: 42, md: 48 },
                height: compact ? 34 : { xs: 42, sm: 52, md: 60 },
                objectFit: 'contain',
                display: 'block',
                mb: 0.75,
                filter: isDark
                  ? 'drop-shadow(0 2px 6px rgba(0,0,0,0.45))'
                  : 'drop-shadow(0 2px 5px rgba(0,0,0,0.12))',
              }}
            />
            <Typography
              sx={{
                fontFamily: 'PremierLeagueBold',
                fontSize: compact ? '0.68rem' : { xs: '0.78rem', sm: '0.86rem', md: '0.94rem' },
                lineHeight: 1.25,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                color: '#fff',
                textShadow:
                  '0 0 1px rgba(0,0,0,0.95), 0 1px 3px rgba(0,0,0,0.85), 0 2px 10px rgba(0,0,0,0.55)',
              }}
            >
              {r.player?.web_name ?? '—'}
            </Typography>
            {statPill(r)}
          </Box>
        )
      })}
    </Box>
  )

  const similarityBadge = (pct: number) => (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.15,
        px: compact ? 0.65 : { xs: 0.85, sm: 1.1 },
        py: compact ? 0.35 : { xs: 0.45, sm: 0.55 },
        borderRadius: 1.5,
        backgroundColor: isDark ? 'rgba(0,0,0,0.72)' : 'rgba(255,255,255,0.94)',
        border: '1px solid',
        borderColor: isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.12)',
        boxShadow: '0 2px 10px rgba(0,0,0,0.28)',
        minWidth: compact ? 58 : { xs: 72, sm: 80 },
      }}
    >
      <Typography
        sx={{
          fontFamily: 'PremierLeagueBold',
          fontSize: compact ? '0.4rem' : '0.44rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          opacity: 0.65,
          lineHeight: 1.2,
          textAlign: 'center',
          color: isDark ? '#fff' : 'inherit',
        }}
      >
        Similarity
      </Typography>
      <Typography
        sx={{
          fontFamily: 'PremierLeagueBold',
          fontSize: compact ? '0.72rem' : { xs: '0.82rem', sm: '0.9rem' },
          lineHeight: 1,
          color: isDark ? '#fff' : 'inherit',
        }}
      >
        {formatDecimal(pct)}%
      </Typography>
    </Box>
  )

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        variant="h6"
        sx={{
          fontFamily: 'PremierLeagueBold',
          fontSize: { xs: '1rem', sm: '1.25rem' },
          lineHeight: 1.2,
          mb: 1.25,
        }}
      >
        League Template XI
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <TemplatePitchSurface
          borderRadius={compact ? 1.5 : 2}
          overlay={
            showSimilarity ? (
              <Box
                sx={{
                  position: 'absolute',
                  top: compact ? '8%' : { xs: '9%', sm: '10%' },
                  right: compact ? '7%' : { xs: '8%', sm: '9%' },
                  zIndex: 3,
                  pointerEvents: 'none',
                }}
              >
                {similarityBadge(jacLeaguePct!)}
              </Box>
            ) : null
          }
        >
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-evenly',
              alignItems: 'center',
              width: '100%',
              px: compact ? 1.5 : { xs: 2.5, sm: 3.5, md: 4 },
              py: compact ? 0.75 : { xs: 1.25, sm: 1.75, md: 2 },
              boxSizing: 'border-box',
            }}
          >
            {TEMPLATE_PITCH_POS_ORDER.map((elementType) => {
              const rows = byType[elementType]
              if (!rows.length) return null
              return <React.Fragment key={elementType}>{rowOfCells(rows)}</React.Fragment>
            })}
          </Box>
        </TemplatePitchSurface>
      </Box>
    </Box>
  )
}

export default LeagueTemplateCard
